/**
 * BusinessLayout (TMMRSL-0058, MM-D-107) — the provider on `/business/*`. Loads the reseller's
 * one tenant via `GET /v1/reseller-admin/tenant` (`getMyTenant`) and supplies it through the same
 * `useTenant()` / `useTenantApi()` every per-tenant screen already uses. Renders only the outlet —
 * no header or tabs; the Overview carries the business identity.
 *
 *  - Loads once per visit to the section.
 *  - `404 TENANT_NOT_FOUND` on a per-tenant call → re-run the load (no toast, no navigate away).
 *  - No tenant yet → a clear empty state with a link back to the Dashboard.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { backendCode } from '@/api/errors';
import { resellerAdminApi, type ResellerTenantView } from '@/api/resellerAdmin';
import { onTenantGone } from '@/api/tenantGone';
import { ApiErrorState } from '@/components/ApiErrorState';
import { useApiErrorHandler } from '@/components/useApiErrorHandler';
import { HOME_PATH } from '@/routes/home';
import { TenantContext, type TenantContextValue } from '@/tenant/tenantContext';

type LoadState =
  | { kind: 'pending' }
  | { kind: 'failed'; error: unknown }
  | { kind: 'ready'; tenant: ResellerTenantView | null };

export function BusinessLayout() {
  const { t } = useTranslation();
  const handleApiError = useApiErrorHandler();
  const [state, setState] = useState<LoadState>({ kind: 'pending' });
  const started = useRef(false);
  const handlerRef = useRef(handleApiError);
  const slugRef = useRef<string | null>(null);

  useEffect(() => {
    handlerRef.current = handleApiError;
  }, [handleApiError]);

  const attempt = useCallback(() => {
    setState({ kind: 'pending' });
    resellerAdminApi.getMyTenant().then(
      (tenant) => {
        slugRef.current = tenant?.slug ?? null;
        setState({ kind: 'ready', tenant });
      },
      (error: unknown) => setState({ kind: 'failed', error }),
    );
  }, []);

  const reload = useCallback(async () => {
    const tenant = await resellerAdminApi.getMyTenant();
    slugRef.current = tenant?.slug ?? null;
    setState({ kind: 'ready', tenant });
  }, []);

  // Stable across renders, so `useTenantApi()` (and every hook keyed on it) stays stable.
  const onApiError = useCallback((err: unknown) => {
    if (backendCode(err) === 'RESELLER_ROLE_FORBIDDEN') handlerRef.current(err);
  }, []);

  useEffect(
    () =>
      onTenantGone((gone) => {
        if (slugRef.current !== null && gone !== slugRef.current) return;
        attempt();
      }),
    [attempt],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    attempt();
  }, [attempt]);

  const value = useMemo<TenantContextValue | null>(
    () => (state.kind === 'ready' && state.tenant ? { ...state.tenant, reload, onApiError } : null),
    [state, reload, onApiError],
  );

  if (state.kind === 'failed') {
    return <ApiErrorState what={t('errors.what.businessOverview')} error={state.error} onRetry={attempt} />;
  }

  if (state.kind === 'pending') {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}
      >
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  }

  if (state.tenant === null) {
    return (
      <Box component="section">
        <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
          {t('business.none.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {t('business.none.body')}
        </Typography>
        <Link component={RouterLink} to={HOME_PATH}>
          {t('business.none.dashboardLink')}
        </Link>
      </Box>
    );
  }

  return (
    <TenantContext.Provider value={value}>
      <Outlet />
    </TenantContext.Provider>
  );
}
